(() => {
  'use strict';
  if (window.SuitYouSaved) return;
  const key = 'suityou.savedItems.v1';
  const legacyKeys = ['suityou.savedResources.v1', 'suityouSavedInsights'];
  const listeners = new Set();
  const text = (value, max = 160) => typeof value === 'string' ? value.trim().slice(0, max) : '';
  const parse = (value) => { try { return JSON.parse(value); } catch { return null; } };
  const position = (value) => Number.isFinite(parseFloat(value)) ? Math.max(0, Math.min(100, parseFloat(value))) : 50;
  const normalize = (value) => {
    if (!value || typeof value !== 'object') return null;
    const type = value.type === 'article' ? 'article' : value.type === 'resource' ? 'resource' : null;
    if (!type) return null;
    let handle = text(value.handle, 240);
    let url = '';
    try {
      const candidate = new URL(value.url || (type === 'article' ? `/blogs/${handle}` : `/products/${handle}`), location.origin);
      const pattern = type === 'article' ? /^\/blogs\/([a-z0-9-]+\/[a-z0-9-]+)\/?$/ : /^\/products\/([a-z0-9-]+)\/?$/;
      const match = candidate.pathname.match(pattern);
      if (candidate.origin !== location.origin || !match) return null;
      handle = match[1];
      url = candidate.pathname.replace(/\/$/, '');
    } catch { return null; }
    let image = '';
    try {
      if (!text(value.image, 2000)) throw new Error('No image');
      const candidate = new URL(value.image, location.origin);
      if (candidate.protocol === 'https:' && [location.hostname, 'cdn.shopify.com'].includes(candidate.hostname)) image = candidate.href;
    } catch { /* Missing images use the card's icon fallback. */ }
    return {
      type, id: text(String(value.id || handle), 240), handle, title: text(value.title, 500), url, image,
      imageFit: value.imageFit === 'cover' ? 'cover' : 'contain',
      imagePositionX: position(value.imagePositionX), imagePositionY: position(value.imagePositionY),
      imageWidth: Math.max(0, Math.min(20000, parseInt(value.imageWidth, 10) || 0)),
      imageHeight: Math.max(0, Math.min(20000, parseInt(value.imageHeight, 10) || 0)),
      format: text(value.format), world: text(value.world), topic: text(value.topic), level: text(value.level, 80),
      readTime: Math.max(0, Math.min(240, parseInt(value.readTime, 10) || 0)),
      savedAt: Number.isFinite(value.savedAt) && value.savedAt > 0 ? value.savedAt : 0,
    };
  };
  const identity = (item) => `${item.type}:${item.handle}`;
  const normalizeList = (values) => {
    const unique = new Map();
    if (!Array.isArray(values)) return [];
    values.map(normalize).filter(Boolean).sort((a, b) => b.savedAt - a.savedAt).forEach((item) => {
      if (!unique.has(identity(item))) unique.set(identity(item), item);
    });
    return [...unique.values()];
  };
  const read = () => {
    try {
      const stored = parse(localStorage.getItem(key));
      return normalizeList(Array.isArray(stored) ? stored : stored?.items);
    } catch { return []; }
  };
  const notify = () => {
    const items = read();
    listeners.forEach((listener) => listener(items));
    window.dispatchEvent(new CustomEvent('suityou:saved-changed', { detail: { count: items.length } }));
  };
  const write = (items) => {
    try {
      localStorage.setItem(key, JSON.stringify({ version: 1, items: normalizeList(items) }));
      notify();
      return true;
    } catch { return false; }
  };
  // Retire the two old stores only after the merged records are durably written.
  const migrate = () => {
    try {
      const resources = parse(localStorage.getItem(legacyKeys[0]));
      const articles = parse(localStorage.getItem(legacyKeys[1]));
      if (!resources && !articles) return;
      const old = [
        ...(Array.isArray(resources) ? resources.map((item) => ({ ...item, type: 'resource' })) : []),
        ...(Array.isArray(articles) ? articles.map((item) => typeof item === 'string'
          ? { type: 'article', handle: item, title: '', savedAt: 0 } : { ...item, type: 'article' }) : []),
      ];
      if (write([...read(), ...old])) legacyKeys.forEach((oldKey) => localStorage.removeItem(oldKey));
    } catch { /* Blocked storage must never interrupt storefront initialization. */ }
  };
  const has = (item) => { const valid = normalize(item); return !!valid && read().some((saved) => identity(saved) === identity(valid)); };
  const add = (item) => {
    const valid = normalize(item);
    if (!valid) return false;
    const items = read();
    const existing = items.find((saved) => identity(saved) === identity(valid));
    return write([{ ...valid, savedAt: existing?.savedAt || Date.now() }, ...items.filter((saved) => identity(saved) !== identity(valid))]);
  };
  const remove = (item) => { const valid = normalize(item); return !!valid && write(read().filter((saved) => identity(saved) !== identity(valid))); };
  const update = (item) => {
    const valid = normalize(item);
    if (!valid || !has(valid)) return false;
    return write(read().map((saved) => identity(saved) === identity(valid) ? { ...valid, savedAt: saved.savedAt } : saved));
  };
  window.SuitYouSaved = Object.freeze({ key, normalize, list: read, has, add, remove, update,
    toggle: (item) => has(item) ? remove(item) : add(item), count: () => read().length,
    subscribe: (listener) => { listeners.add(listener); return () => listeners.delete(listener); },
  });
  migrate();
  window.addEventListener('storage', (event) => { if (!event.key || event.key === key) notify(); });
  window.addEventListener('pageshow', notify);

  const fromElement = (element) => {
    const data = element.dataset;
    const root = element.closest('[data-sy-resource-detail]');
    return normalize({ type: data.sySavedType, id: data.sySavedId, handle: data.sySavedHandle,
      title: data.sySavedTitle, url: data.sySavedUrl, image: data.sySavedImage,
      imageFit: data.sySavedImageFit, imagePositionX: data.sySavedImagePositionX, imagePositionY: data.sySavedImagePositionY,
      imageWidth: data.sySavedImageWidth, imageHeight: data.sySavedImageHeight,
      format: data.sySavedFormat, world: data.sySavedWorld || root?.dataset.worldLabel,
      topic: data.sySavedTopic, level: data.sySavedLevel, readTime: data.sySavedReadTime, savedAt: Date.now() });
  };
  const svg = (name) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    element.setAttribute('viewBox', '0 0 24 24');
    element.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(element.namespaceURI, 'path');
    // Existing SY Learning Platform Lucide Bookmark / BookOpen paths (ISC).
    path.setAttribute('d', name === 'bookmark' ? 'M6 3h12v18l-6-4-6 4V3Z' : 'M12 7v14M3 3h4a5 5 0 0 1 5 4 5 5 0 0 1 5-4h4v17h-4a5 5 0 0 0-5 1 5 5 0 0 0-5-1H3z');
    element.append(path);
    return element;
  };
  let statusTimer;
  const status = (button, message) => {
    let region = button.closest('[data-sy-resource-detail]')?.querySelector('[data-sy-action-status]');
    if (!region) {
      region = document.querySelector('[data-sy-save-status]');
      if (!region) {
        region = document.createElement('p');
        region.className = 'sy-saved-status';
        region.dataset.sySaveStatus = '';
        region.setAttribute('role', 'status');
        region.setAttribute('aria-live', 'polite');
        document.body.append(region);
      }
    }
    region.textContent = message;
    clearTimeout(statusTimer);
    statusTimer = setTimeout(() => { region.textContent = ''; }, 5000);
  };
  const selector = 'button[data-sy-saved-type], a.sy-home-one__resource-card[data-sy-saved-type]';
  const refreshControls = () => {
    document.querySelectorAll(selector).forEach((element) => {
      const item = fromElement(element);
      if (!item) return;
      const saved = has(item);
      if (element.tagName === 'A') {
        let badge = element.querySelector('[data-sy-grid-saved]');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'sy-saved-grid-mark'; badge.dataset.syGridSaved = '';
          badge.append(svg('bookmark'), document.createTextNode('Saved'));
          element.append(badge);
        }
        badge.hidden = !saved;
        return;
      }
      element.hidden = false;
      element.setAttribute('aria-pressed', String(saved));
      element.setAttribute('aria-label', saved ? `Remove ${item.title} from saved items` : `Save ${item.title} for later`);
      element.title = saved ? 'Remove from saved items' : 'Save for later';
      if ('syTooltip' in element.dataset) element.dataset.syTooltip = saved ? 'Remove from saved items' : 'Save on this device';
      const label = element.querySelector('span');
      if (label) {
        if (!element.dataset.syUnsavedLabel) element.dataset.syUnsavedLabel = label.textContent;
        label.textContent = saved ? 'Saved' : element.dataset.syUnsavedLabel;
      }
      if (item.type === 'article') {
        if (!element.parentElement.matches('.sy-saved-article-actions')) {
          const actions = document.createElement('div');
          actions.className = 'sy-saved-article-actions';
          element.before(actions);
          actions.append(element);
        }
        let link = element.nextElementSibling;
        if (!link?.matches('[data-sy-article-view-saved]')) {
          link = document.createElement('a');
          link.href = '/pages/saved'; link.textContent = 'View saved';
          link.className = 'sy-saved-view'; link.dataset.syArticleViewSaved = '';
          element.after(link);
        }
        link.hidden = !saved;
      }
    });
    const count = read().length;
    document.querySelectorAll('[data-sy-saved-count]').forEach((badge) => { badge.textContent = String(count); badge.hidden = count === 0; });
  };
  const initialize = () => {
    refreshControls();
    listeners.add(refreshControls);
    document.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-sy-saved-type]');
      if (!button) return;
      const item = fromElement(button);
      if (!item) return;
      event.preventDefault();
      const wasSaved = has(item);
      status(button, window.SuitYouSaved.toggle(item) ? (wasSaved ? 'Removed from saved items.' : 'Saved on this device.') : 'Saving is unavailable in this browser.');
    });
    // Resource filters load new card markup without reloading the page.
    const observer = new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))))) refreshControls();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    initializeSavedPage();
  };

  const initializeSavedPage = () => {
    const page = document.querySelector('[data-sy-saved-page]');
    if (!page) return;
    const grid = page.querySelector('[data-sy-saved-grid]');
    const empty = page.querySelector('[data-sy-saved-empty]');
    const filteredEmpty = page.querySelector('[data-sy-saved-filter-empty]');
    const resultsStatus = page.querySelector('[data-sy-saved-page-status]');
    const checks = new Map();
    let filter = 'all';
    const labels = { ebook: 'Ebook', guide: 'Guide', workbook: 'Workbook', checklist: 'Checklist', audiobook: 'Audiobook', 'video-course': 'Video course', 'ebooks-guides': 'Ebooks & Guides', 'video-courses': 'Video courses', 'templates-tools': 'Templates & Tools', 'audiobooks': 'Audiobooks' };
    const render = () => {
      const items = read();
      const visible = items.filter((item) => filter === 'all' || item.type === filter);
      const focused = document.activeElement?.dataset.sySavedRemove;
      const fragment = document.createDocumentFragment();
      visible.forEach((item) => {
        const checked = checks.get(identity(item));
        const available = checked?.state === 'available';
        const card = document.createElement('article');
        card.className = `sy-saved-card sy-saved-card--${item.type}`;
        card.dataset.sySavedCard = identity(item);
        const media = document.createElement('div'); media.className = 'sy-saved-card__media';
        media.append(svg(item.type === 'article' ? 'bookmark' : 'book'));
        if (item.image) {
          const image = document.createElement('img'); image.src = item.image; image.alt = ''; image.loading = 'lazy';
          image.width = item.imageWidth || 720; image.height = item.imageHeight || (item.type === 'article' ? 448 : 720);
          if (item.type === 'article') {
            image.style.objectFit = item.imageFit;
            image.style.objectPosition = `${item.imagePositionX}% ${item.imagePositionY}%`;
          }
          image.addEventListener('error', () => image.remove(), { once: true }); media.append(image);
        }
        const copy = document.createElement('div'); copy.className = 'sy-saved-card__copy';
        const category = document.createElement('p'); category.className = 'sy-saved-card__category';
        category.textContent = item.type === 'article' ? item.topic || item.world || 'Article' : labels[item.format] || item.format || 'Resource';
        const title = document.createElement('h2'); title.textContent = item.title || item.handle.split('/').pop().replace(/-/g, ' ');
        const meta = document.createElement('p'); meta.className = 'sy-saved-card__meta';
        meta.textContent = item.type === 'article' ? (item.readTime ? `${item.readTime} min read` : '') : [item.world, item.level].filter(Boolean).join(' · ');
        const availability = document.createElement('p'); availability.className = 'sy-saved-card__availability';
        availability.textContent = available ? item.type === 'resource' && document.body.dataset.member === 'true' ? checked.memberAccess ? 'Included in your membership' : 'Locked · Preview available' : '' : checked?.state === 'unavailable' ? 'This item is no longer available.' : checked?.state === 'retry' ? 'Availability could not be checked.' : 'Checking availability…';
        const actions = document.createElement('div'); actions.className = 'sy-saved-card__actions';
        if (available) {
          const link = document.createElement('a'); link.href = item.url; link.textContent = item.type === 'article' ? 'Open article' : document.body.dataset.member === 'true' ? checked.memberAccess ? 'Open resource' : 'Preview resource' : 'Explore resource'; actions.append(link);
        } else if (checked?.state === 'retry') {
          const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Try again';
          retry.addEventListener('click', () => { checks.delete(identity(item)); verifyItems(); }); actions.append(retry);
        }
        const removeButton = document.createElement('button'); removeButton.type = 'button'; removeButton.textContent = 'Remove';
        removeButton.dataset.sySavedRemove = identity(item); removeButton.setAttribute('aria-label', `Remove ${title.textContent} from saved items`);
        removeButton.addEventListener('click', () => {
          if (!remove(item)) resultsStatus.textContent = 'Saving is unavailable in this browser.';
          else resultsStatus.textContent = 'Removed from saved items.';
        });
        actions.append(removeButton); copy.append(category, title);
        if (meta.textContent) copy.append(meta);
        if (availability.textContent) copy.append(availability);
        copy.append(actions); card.append(media, copy); fragment.append(card);
      });
      grid.replaceChildren(fragment); grid.hidden = visible.length === 0;
      empty.hidden = items.length > 0; filteredEmpty.hidden = items.length === 0 || visible.length > 0;
      if (focused) (grid.querySelector(`[data-sy-saved-remove="${focused}"]`) || grid.querySelector('button') || page.querySelector('[data-sy-saved-filter]')).focus({ preventScroll: true });
    };
    const verifyItem = async (item) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(item.url, { signal: controller.signal, credentials: 'same-origin' });
        if ([404, 410].includes(response.status)) return { state: 'unavailable' };
        if (!response.ok) return { state: 'retry' };
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const candidates = [...html.querySelectorAll('button[data-sy-saved-type]')];
        const element = candidates.find((candidate) => { const record = fromElement(candidate); return record && identity(record) === identity(item); });
        if (!element || new URL(response.url).origin !== location.origin) return { state: 'unavailable' };
        const fresh = fromElement(element);
        if (JSON.stringify({ ...fresh, savedAt: item.savedAt }) !== JSON.stringify(item)) update({ ...fresh, savedAt: item.savedAt });
        return { state: 'available', memberAccess: element.closest('[data-sy-resource-detail]')?.dataset.memberAccess === 'true' };
      } catch { return { state: 'retry' }; }
      finally { clearTimeout(timeout); }
    };
    let verifying = false;
    const verifyItems = async () => {
      if (verifying) return;
      verifying = true;
      try {
        const pending = read().filter((item) => !checks.has(identity(item)));
        // Three public-page requests at a time; never read admin or unpublished data.
        for (let offset = 0; offset < pending.length; offset += 3) {
          await Promise.all(pending.slice(offset, offset + 3).map(async (item) => {
            checks.set(identity(item), { state: 'checking' });
            checks.set(identity(item), await verifyItem(item));
          }));
          render();
        }
      } finally { verifying = false; }
      if (read().some((item) => !checks.has(identity(item)))) verifyItems();
    };
    page.querySelectorAll('[data-sy-saved-filter]').forEach((button) => button.addEventListener('click', () => {
      filter = button.dataset.sySavedFilter;
      page.querySelectorAll('[data-sy-saved-filter]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
      render();
    }));
    listeners.add(() => { render(); verifyItems(); });
    render(); verifyItems();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize);
  else initialize();
})();
